import { createApi } from "@reduxjs/toolkit/query/react";
import * as logger from "common/logger";
import * as config from "config";
import * as FileSaver from "file-saver";
import * as utils from "utils";

const baseQuery = async (args, api, extraOptions) => {
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.admin(args.url),
      args.payload || {}
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    return {
      error: String(e),
    };
  }
};

// Flatten anything thrown by authenticatedFetch into {type, message, body}: a
// HandledError carries {name, description, body}, a plain Error only
// {name, message}. `body` is the structured payload of a ClientException
// (for example template validation reports) or null.
export const toStructuredError = (e) => ({
  type: e.type ?? e.name ?? "UNKNOWN",
  message: e.description ?? e.message ?? String(e),
  body: e.body ?? null,
});

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: baseQuery,
  tagTypes: ["StagedConfig", "StaticData"],
  endpoints: (builder) => ({
    listTemplates: builder.query({
      query: () => ({
        url: "templates",
      }),
    }),
    listCompanies: builder.query({
      query: () => ({
        url: "companies",
      }),
    }),
    createCompany: builder.mutation({
      query: ({ name, template, users }) => ({
        url: "companies",
        payload: {
          body: JSON.stringify({
            name,
            filesTemplateKey: template,
            users,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    getAllowedUserPermissions: builder.query({
      query: () => ({
        url: "users/retrieve-privilege-list",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      }),
    }),
    createUser: builder.mutation({
      query: ({
        email,
        enabled,
        perms,
        timeout,
        sendEmail,
        setPassword,
        password,
        companyId,
        userLogMessage,
      }) => ({
        url: "users",
        payload: {
          body: JSON.stringify({
            companyId,
            email,
            enabled,
            perms,
            timeout,
            sendEmail,
            setPassword,
            password,
            userLogMessage,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    configureUser: builder.mutation({
      query: ({
        username,
        enabled,
        perms,
        timeoutAdd,
        timeoutDelete,
        userLogMessage,
      }) => ({
        url: "users/configure",
        payload: {
          body: JSON.stringify({
            username,
            enabled,
            perms,
            timeoutAdd,
            timeoutDelete,
            userLogMessage,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    resetUserPassword: builder.mutation({
      query: ({ username, userLogMessage, companyId }) => ({
        url: "users/reset-password",
        payload: {
          body: JSON.stringify({
            username,
            userLogMessage,
            companyId,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    listUsers: builder.query({
      query: ({ companyId }) => ({
        url: `users?companyId=${companyId}`,
        payload: {
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    downloadCompanyUserAclPdf: builder.mutation({
      queryFn: async ({ companyId, activeOnly = false }) => {
        try {
          const response = await utils.authenticatedFetch(
            config.endpoints.admin("users/acl-pdf"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ companyId, activeOnly }),
            }
          );
          const { presignedUrl } = await response.json();

          const fileResponse = await fetch(presignedUrl);
          const blob = await fileResponse.blob();
          FileSaver.saveAs(
            blob,
            `users-acl-${companyId}${activeOnly ? "-active" : ""}.pdf`
          );

          return { data: "succeeded" };
        } catch (e) {
          logger.exception(e);
          return {
            error: String(e),
          };
        }
      },
    }),
    // Whitelists (auditor submission access)
    getUserWhitelistList: builder.query({
      query: ({ userId }) => ({
        url: `submission-list-permissions/${userId}`,
      }),
    }),
    updateUserWhitelistList: builder.mutation({
      query: ({ userId, submissionIds, allSubmissionsAllowed }) => ({
        url: `submission-list-permissions/${userId}`,
        payload: {
          method: "put",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            submission_id_whitelist: submissionIds,
            all_submissions_allowed: allSubmissionsAllowed ?? false,
          }),
        },
      }),
    }),
    // Whitelists (auditor submission access)
    getUserWhitelist: builder.query({
      query: ({ userId }) => ({
        url: `submission-permissions/${userId}`,
      }),
    }),
    updateUserWhitelist: builder.mutation({
      query: ({ userId, submissionIds, allSubmissionsAllowed }) => ({
        url: `submission-permissions/${userId}`,
        payload: {
          method: "put",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            submission_id_whitelist: submissionIds,
            all_submissions_allowed: allSubmissionsAllowed ?? false,
          }),
        },
      }),
    }),
    listStaticData: builder.query({
      query: ({ companyId }) => ({
        url: `static-data/${companyId}`,
      }),
      providesTags: [{ type: "StaticData", id: "LIST" }],
    }),
    listAuditLogs: builder.query({
      query: ({ companyId, startDate, endDate, email }) => {
        const query = [];
        if (startDate) query.push(`start-date=${startDate}`);
        if (endDate) query.push(`end-date=${endDate}`);
        if (email) query.push(`email=${email}`);
        const qryTxt = query ? "?" + query.join("&") : "";
        return {
          url: `list-audit-logs/${companyId}${qryTxt}`,
        };
      },
    }),
    createAuditLog: builder.mutation({
      query: ({ companyId, userMessage }) => ({
        url: "audit-logs",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId,
            userMessage,
          }),
        },
      }),
    }),
    getStaticData: builder.query({
      query: ({ companyId, key }) => ({
        url: `static-data/${companyId}/${key}`,
      }),
    }),
    getDefaultStaticData: builder.query({
      query: ({ key }) => ({
        url: `static-data/default/${key}`,
      }),
    }),
    getOverlayStaticData: builder.query({
      query: ({ key }) => ({
        url: `static-data/overlay/${key}`,
      }),
    }),
    listStaticDataVersions: builder.query({
      query: ({ companyId, key }) => ({
        url: `static-data/${companyId}/${key}/versions`,
      }),
    }),
    getStaticDataVersion: builder.query({
      query: ({ companyId, key, versionId }) => ({
        url: `static-data/${companyId}/${key}/versions/${versionId}`,
      }),
    }),
    updateStaticData: builder.mutation({
      // A queryFn rather than a query so that a TEMPLATE_VALIDATION_FAILED
      // refusal reaches the component with its reports in error.body; the
      // shared baseQuery flattens errors to a string.
      queryFn: async ({ companyId, key, data, userLogMessage, force }) => {
        try {
          const response = await utils.authenticatedFetch(
            config.endpoints.admin(`static-data/${companyId}/${key}`),
            {
              method: "post",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                data,
                userLogMessage,
                ...(force !== undefined && { force }),
              }),
            }
          );
          return { data: await response.json() };
        } catch (e) {
          return { error: toStructuredError(e) };
        }
      },
      invalidatesTags: [{ type: "StaticData", id: "LIST" }],
    }),
    listStagedConfig: builder.query({
      query: ({ status, companyId } = {}) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (companyId) params.set("company-id", companyId);
        const queryString = params.toString();
        return {
          url: queryString ? `staged-config?${queryString}` : "staged-config",
        };
      },
      providesTags: [{ type: "StagedConfig", id: "LIST" }],
    }),
    getStagedConfig: builder.query({
      query: ({ companyId, id }) => ({
        url: `staged-config/${companyId}/${id}`,
      }),
      providesTags: (result, error, { id }) => [{ type: "StagedConfig", id }],
    }),
    createStagedConfig: builder.mutation({
      query: ({
        companyId,
        changeType,
        key,
        data,
        title,
        prNumbers,
        githubLink,
        reason,
      }) => ({
        url: "staged-config",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId,
            changeType,
            key,
            data,
            title,
            prNumbers,
            githubLink,
            reason,
          }),
        },
      }),
      invalidatesTags: [{ type: "StagedConfig", id: "LIST" }],
    }),
    updateStagedConfig: builder.mutation({
      query: ({
        companyId,
        id,
        data,
        title,
        prNumbers,
        githubLink,
        reason,
      }) => ({
        url: `staged-config/${companyId}/${id}`,
        payload: {
          method: "put",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data,
            title,
            prNumbers,
            githubLink,
            reason,
          }),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StagedConfig", id: "LIST" },
        { type: "StagedConfig", id },
      ],
    }),
    approveStagedConfig: builder.mutation({
      query: ({ companyId, id }) => ({
        url: `staged-config/${companyId}/${id}/approve`,
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StagedConfig", id: "LIST" },
        { type: "StagedConfig", id },
      ],
    }),
    rejectStagedConfig: builder.mutation({
      query: ({ companyId, id }) => ({
        url: `staged-config/${companyId}/${id}/reject`,
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "StagedConfig", id: "LIST" },
        { type: "StagedConfig", id },
      ],
    }),
    applyStagedConfig: builder.mutation({
      query: ({ changes }) => ({
        url: "staged-config/apply",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ changes }),
        },
      }),
      invalidatesTags: [
        { type: "StagedConfig", id: "LIST" },
        { type: "StaticData", id: "LIST" },
      ],
    }),
    validatePricingModelWorkbook: builder.mutation({
      query: ({ companyId, fileId }) => ({
        url: `pricing-models/${companyId}/validate-workbook`,
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileId,
          }),
        },
      }),
    }),
    promotePricingModelWorkbook: builder.mutation({
      query: ({ companyId, fileId, model, version, userLogMessage }) => ({
        url: `pricing-models/${companyId}/promote-workbook`,
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileId,
            model,
            version,
            userLogMessage,
          }),
        },
      }),
    }),
    adminUpdateNavigationConfig: builder.mutation({
      query: ({ companyId, config, userLogMessage }) => ({
        url: "admin-update-navigation-config",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId,
            config,
            userLogMessage,
          }),
        },
      }),
    }),
    adminUpdateNotificationConfig: builder.mutation({
      query: ({ companyId, config, userLogMessage }) => ({
        url: "admin-update-notification-config",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId,
            config,
            userLogMessage,
          }),
        },
      }),
    }),
    updateUserConfig: builder.mutation({
      query: ({ companyId, userLogMessage, value }) => ({
        url: "user-config",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId,
            userLogMessage,
            value,
          }),
        },
      }),
    }),
    adminRetrieveMaintenanceStatus: builder.query({
      query: () => ({
        url: "admin-retrieve-maintenance-status",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      }),
    }),
    adminUpdateMaintenanceStatus: builder.mutation({
      query: ({ message, showWarningNow, disableNow, versionNumber }) => ({
        url: "admin-update-maintenance-status",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message,
            showWarningNow,
            disableNow,
            versionNumber,
          }),
        },
      }),
    }),
    getDeploymentPipelineStatus: builder.query({
      query: () => ({
        url: "deployment-pipeline/status",
      }),
    }),
    listDeploymentPipelineExecutions: builder.query({
      query: () => ({
        url: "deployment-pipeline/executions",
      }),
    }),
    getDeploymentPipelineExecutionLogs: builder.query({
      query: ({ executionId, nextToken }) => {
        const url = nextToken
          ? `deployment-pipeline/executions/${executionId}/logs?next_token=${encodeURIComponent(
              nextToken
            )}`
          : `deployment-pipeline/executions/${executionId}/logs`;
        return { url };
      },
    }),
    getDeployableServices: builder.query({
      query: () => ({
        url: "deployment-pipeline/services",
      }),
    }),
    getServiceDeployments: builder.query({
      query: () => ({
        url: "deployment-pipeline/deployments",
      }),
    }),
    getSiteDeployments: builder.query({
      query: ({ nextToken } = {}) => ({
        url: nextToken
          ? `deployment-pipeline/site-deployments?next_token=${encodeURIComponent(
              nextToken
            )}`
          : "deployment-pipeline/site-deployments",
      }),
    }),
    getSiteDeploymentLogs: builder.query({
      query: (jobId) => ({
        url: `deployment-pipeline/site-deployments/${jobId}/logs`,
      }),
    }),
    triggerDeploymentPipeline: builder.mutation({
      query: ({ serviceNames } = {}) => ({
        url: "deployment-pipeline/trigger",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ serviceNames }),
        },
      }),
    }),
    listAllActiveDemoRuns: builder.query({
      query: () => ({
        url: "demo-data/active-runs",
      }),
    }),
    listDemoRuns: builder.query({
      query: ({ companyId }) => ({
        url: `demo-data/${companyId}/runs`,
      }),
    }),
    getDemoRunStatus: builder.query({
      query: ({ companyId, runId }) => ({
        url: `demo-data/${companyId}/runs/${runId}/status`,
      }),
    }),
    getDemoRunProfile: builder.query({
      // The API returns a presigned S3 link: big profiles outgrow a Lambda response
      queryFn: async ({ companyId, runId }) => {
        try {
          const response = await utils.authenticatedFetch(
            config.endpoints.admin(
              `demo-data/${companyId}/runs/${runId}/profile`
            )
          );
          const { url } = await response.json();
          const profileResponse = await fetch(url);
          if (!profileResponse.ok) {
            throw new Error(
              `Profile download failed (${profileResponse.status})`
            );
          }
          return { data: await profileResponse.json() };
        } catch (e) {
          logger.exception(e);
          return { error: String(e) };
        }
      },
    }),
    getDemoRunLog: builder.query({
      query: ({ companyId, runId }) => ({
        url: `demo-data/${companyId}/runs/${runId}/log`,
      }),
    }),
    getDemoRunDownload: builder.query({
      query: ({ companyId, runId, file }) => ({
        url: `demo-data/${companyId}/runs/${runId}/download?file=${encodeURIComponent(
          file
        )}`,
      }),
    }),
    getDemoRunLoaded: builder.query({
      query: ({ companyId, runId }) => ({
        url: `demo-data/${companyId}/runs/${runId}/loaded`,
      }),
    }),
    triggerDemoProfile: builder.mutation({
      query: ({
        companyId,
        profileLimit,
        ollamaModel,
        maxWorkTimeS,
        instanceType,
        fetchWorkers,
      }) => ({
        url: `demo-data/${companyId}/trigger-profile`,
        payload: {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            profile_limit: profileLimit,
            ollama_model: ollamaModel,
            max_work_time_s: maxWorkTimeS,
            instance_type: instanceType || null,
            fetch_workers: fetchWorkers,
          }),
        },
      }),
    }),
    stopDemoRun: builder.mutation({
      query: ({ companyId, runId }) => ({
        url: `demo-data/${companyId}/runs/${runId}/stop`,
        payload: { method: "post" },
      }),
    }),
    deleteDemoRun: builder.mutation({
      query: ({ companyId, runId }) => ({
        url: `demo-data/${companyId}/runs/${runId}/delete`,
        payload: { method: "post" },
      }),
    }),
    // Writes field_summary.md and field_summary.csv next to the run's profile.json and returns their names
    createDemoRunFieldSummary: builder.mutation({
      query: ({ companyId, runId }) => ({
        url: `demo-data/${companyId}/runs/${runId}/field-summary`,
        payload: { method: "post" },
      }),
    }),
    uploadDemoProfile: builder.mutation({
      // The file goes straight to S3 through a presigned link: profiles outgrow a Lambda request
      queryFn: async ({ companyId, file, name, note }) => {
        try {
          const start = await utils.authenticatedFetch(
            config.endpoints.admin(`demo-data/${companyId}/upload-profile`),
            { method: "POST" }
          );
          const { run_id: runId, upload_url: uploadUrl } = await start.json();
          const upload = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: file,
          });
          if (!upload.ok) {
            throw new Error(`Profile upload failed (${upload.status})`);
          }
          const finish = await utils.authenticatedFetch(
            config.endpoints.admin(
              `demo-data/${companyId}/runs/${runId}/finish-upload`
            ),
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ name, note }),
            }
          );
          return { data: await finish.json() };
        } catch (e) {
          logger.exception(e);
          return { error: String(e) };
        }
      },
    }),
    triggerDemoGenerate: builder.mutation({
      query: ({
        companyId,
        sourceRunId,
        genCount,
        llmProvider,
        llmModel,
        minPresence,
        labelPrefix,
        genSeed,
        maxWorkTimeS,
        loadCompanyId,
        instanceType,
        renewalShare,
        lossRunShare,
      }) => ({
        url: `demo-data/${companyId}/trigger-generate`,
        payload: {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source_run_id: sourceRunId,
            gen_count: genCount,
            llm_provider: llmProvider,
            llm_model: llmModel,
            min_presence: minPresence || null,
            label_prefix: labelPrefix || null,
            max_work_time_s: maxWorkTimeS,
            load_company_id: loadCompanyId || null,
            instance_type: instanceType || null,
            gen_seed: genSeed ? parseInt(genSeed, 10) : null,
            renewal_share: renewalShare ?? null,
            loss_run_share: lossRunShare ?? null,
          }),
        },
      }),
    }),
  }),
});

export const {
  useListTemplatesQuery,
  useListCompaniesQuery,
  useLazyListUsersQuery,
  useDownloadCompanyUserAclPdfMutation,
  useCreateCompanyMutation,
  useCreateUserMutation,
  useConfigureUserMutation,
  useResetUserPasswordMutation,
  useListStaticDataQuery,
  useGetStaticDataQuery,
  useLazyGetStaticDataQuery,
  useGetDefaultStaticDataQuery,
  useGetOverlayStaticDataQuery,
  useListStaticDataVersionsQuery,
  useGetStaticDataVersionQuery,
  useUpdateStaticDataMutation,
  useValidatePricingModelWorkbookMutation,
  usePromotePricingModelWorkbookMutation,
  useListStagedConfigQuery,
  useGetStagedConfigQuery,
  useCreateStagedConfigMutation,
  useUpdateStagedConfigMutation,
  useApproveStagedConfigMutation,
  useRejectStagedConfigMutation,
  useApplyStagedConfigMutation,
  useAdminUpdateNavigationConfigMutation,
  useAdminUpdateNotificationConfigMutation,
  useListAuditLogsQuery,
  useCreateAuditLogMutation,
  useUpdateUserConfigMutation,
  useAdminRetrieveMaintenanceStatusQuery,
  useAdminUpdateMaintenanceStatusMutation,
  useGetAllowedUserPermissionsQuery,
  useGetUserWhitelistQuery,
  useUpdateUserWhitelistMutation,
  useGetUserWhitelistListQuery,
  useUpdateUserWhitelistListMutation,
  useGetDeployableServicesQuery,
  useGetServiceDeploymentsQuery,
  useGetSiteDeploymentsQuery,
  useLazyGetSiteDeploymentsQuery,
  useGetSiteDeploymentLogsQuery,
  useGetDeploymentPipelineStatusQuery,
  useListDeploymentPipelineExecutionsQuery,
  useLazyGetDeploymentPipelineExecutionLogsQuery,
  useTriggerDeploymentPipelineMutation,
  useListAllActiveDemoRunsQuery,
  useListDemoRunsQuery,
  useLazyListDemoRunsQuery,
  useGetDemoRunStatusQuery,
  useLazyGetDemoRunStatusQuery,
  useGetDemoRunProfileQuery,
  useLazyGetDemoRunProfileQuery,
  useGetDemoRunLogQuery,
  useLazyGetDemoRunLogQuery,
  useLazyGetDemoRunDownloadQuery,
  useGetDemoRunLoadedQuery,
  useTriggerDemoProfileMutation,
  useTriggerDemoGenerateMutation,
  useStopDemoRunMutation,
  useUploadDemoProfileMutation,
  useDeleteDemoRunMutation,
  useCreateDemoRunFieldSummaryMutation,
} = adminApi;
